import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { AuthLayout } from "../components/AuthLayout";
import { SuburbAutocomplete } from "../components/SuburbAutocomplete";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SocialAuthButtons } from "../components/SocialAuthButtons";
import { PasswordInput } from "../components/PasswordInput";
import { DemoVideo } from "../components/DemoVideo";
import { errText } from "../lib/api";

export default function SignUp() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    clinicName: "",
    suburb: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    const { firstName, lastName, clinicName, suburb, email, password } = form;
    const { data, error } = await signUp({ firstName, lastName, clinicName, suburb, email, password });
    setLoading(false);
    if (error) {
      const m = errText(error, "Sign up failed. Please try again.");
      setError(/already|registered|exists/i.test(m) ? "An account with this email already exists. Please sign in instead." : m);
      toast.error(m);
      return;
    }
    // Supabase returns a user with no identities when the email is already registered.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setError("An account with this email already exists. Please sign in instead.");
      return;
    }
    // If a session is returned (email confirmation disabled), go straight to dashboard.
    if (data?.session) {
      toast.success("Account created! Welcome aboard.");
      navigate("/dashboard", { replace: true });
      return;
    }
    setSuccess(true);
    toast.success("Account created! Please check your email to confirm.");
  };

  if (success) {
    return (
      <AuthLayout title="Check your inbox" subtitle="You're almost there." testId="signup-page">
        <div className="rounded-md border border-accent/30 bg-emerald-50 p-6" data-testid="signup-success-message">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
            <div>
              <p className="font-medium text-slate-900">Registration successful!</p>
              <p className="mt-1 text-sm text-slate-600">
                We've sent a confirmation link to <span className="font-medium">{form.email}</span>.
                Please confirm your email, then sign in to access your dashboard.
              </p>
            </div>
          </div>
        </div>
        <Button asChild className="mt-6 w-full" data-testid="success-go-to-login-button">
          <Link to="/login">Go to sign in</Link>
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Create your account" subtitle="Register your clinic in just a minute." testId="signup-page">
      <div className="mb-5">
        <DemoVideo src="/videos/gp.mp4" title="How GPs use SimplyRefer" buttonLabel="See how it works (60s)" className="w-full" testId="gp-signup-demo" />
      </div>
      <form onSubmit={handleSubmit} className="space-y-5" data-testid="signup-form">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First name</Label>
            <Input id="firstName" required value={form.firstName} onChange={update("firstName")} placeholder="First name" data-testid="signup-firstname-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Last name</Label>
            <Input id="lastName" required value={form.lastName} onChange={update("lastName")} placeholder="Last name" data-testid="signup-lastname-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="clinicName">Clinic name</Label>
          <Input id="clinicName" required value={form.clinicName} onChange={update("clinicName")} placeholder="Your clinic name" data-testid="signup-clinicname-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="suburb">Suburb</Label>
          <SuburbAutocomplete id="suburb" required value={form.suburb} onChange={(value) => setForm((f) => ({ ...f, suburb: value }))} onSelect={(option) => setForm((f) => ({ ...f, suburb: option.suburb }))} placeholder="Start typing a suburb or postcode" data-testid="signup-suburb-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" required value={form.email} onChange={update("email")} placeholder="you@example.com" data-testid="signup-email-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <PasswordInput id="password" required value={form.password} onChange={update("password")} placeholder="At least 6 characters" testId="signup-password-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <PasswordInput id="confirmPassword" required value={form.confirmPassword} onChange={update("confirmPassword")} placeholder="Re-enter your password" testId="signup-confirm-password-input" />
        </div>
        {error && <p className="text-sm text-destructive" data-testid="signup-error">{error}</p>}
        <Button type="submit" className="w-full" disabled={loading} data-testid="signup-submit-button">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create account
        </Button>
      </form>
      <div className="mt-6">
        <SocialAuthButtons label="Or sign up with" />
      </div>
      <p className="mt-6 text-center text-sm text-slate-500">
        Already have an account?{" "}
        <Link to="/login" className="font-medium text-primary hover:underline" data-testid="go-to-login-link">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
